/**
 * 系统命令 — 封装 PWA / 浏览器原生能力
 * 通过 ttx.command 注册，按钮配置 systemCommand 即可调用
 *
 * 使用示例：
 *   按钮 systemCommand = "SHARE"            → 调起系统分享面板
 *   按钮 systemCommand = "FULLSCREEN"        → 进入/退出全屏
 *   按钮 systemCommand = "COPY_TO_CLIPBOARD" → 复制内容到剪切板
 */
window.ttx = window.ttx || {};

// ============================================================
// 内部工具
// ============================================================
var _wakeLockSentinel = null;

/** 释放屏幕唤醒锁 */
function releaseWakeLock() {
    if (_wakeLockSentinel) {
        _wakeLockSentinel.release().catch(function () {});
        _wakeLockSentinel = null;
    }
}

/** 获取按钮所在卡片页的 VM 数据（供 share / copy 等读取内容） */
function getPageData(ctx) {
    try {
        return ctx?.vm?.getData?.() || {};
    } catch (e) {
        return {};
    }
}

/**
 * 系统命令日志包装器：记录命令名、时间、成功/失败
 * 始终返回 Promise（保证 EButton loading 可见），超时 30s 自动 reject
 */
function wrapCommand(name, fn) {
    var MIN_DELAY = 600;
    var MAX_TIMEOUT = 30000;
    return function (ctx) {
        var startTime = Date.now();
        ttx.log.info('[系统命令] ' + name + ' — 调用开始 — ' + new Date(startTime).toLocaleString());

        return new Promise(function (resolve, reject) {
            var finished = false;

            function finish(err, result) {
                if (finished) return;
                finished = true;
                var elapsed = Date.now() - startTime;
                var remaining = Math.max(0, MIN_DELAY - elapsed);
                setTimeout(function () {
                    if (err) {
                        ttx.log.error('[系统命令] ' + name + ' — 失败 — ' + (err.message || '未知错误'));
                        reject(err);
                    } else {
                        ttx.log.success('[系统命令] ' + name + ' — 成功 — 耗时 ' + (Date.now() - startTime) + 'ms');
                        resolve(result);
                    }
                }, remaining);
            }

            // 超时保护：浏览器 API 可能永不 resolve（如桌面端分享无响应）
            setTimeout(function () {
                if (!finished) {
                    ttx.log.warn('[系统命令] ' + name + ' — 超时 — 超过 ' + (MAX_TIMEOUT / 1000) + ' 秒未响应');
                    finish(new Error('操作超时'));
                }
            }, MAX_TIMEOUT);

            try {
                var result = fn(ctx);
                if (result && typeof result.then === 'function') {
                    result.then(function (data) {
                        finish(null, data);
                    }).catch(function (err) {
                        finish(err);
                    });
                } else {
                    finish(null, result);
                }
            } catch (err) {
                finish(err);
            }
        });
    };
}

// ============================================================
// 系统命令
// ============================================================
ttx.command.registerScope('system', {

    /** 系统分享：移动端走 Web Share API，PC 端降级为复制页面链接 + toast 提示 */
    share: wrapCommand('SHARE', function (ctx) {
        var pageUrl = ttx.extend.getPageUrl(ctx);

        // 移动端：使用原生分享面板
        if (ttx.ua.isMobile && navigator.share) {
            var data = getPageData(ctx);
            return navigator.share({
                title: document.title || '',
                text: data.shareText || '',
                url: pageUrl
            }).catch(function (err) {
                if (err.name !== 'AbortError') {
                    ttx.toast.error('分享失败: ' + (err.message || '未知错误'));
                }
            });
        }

        // PC 端降级：复制到剪切板 → toast 提示
        if (navigator.clipboard?.writeText) {
            return navigator.clipboard.writeText(pageUrl).then(function () {
                ttx.toast.success('页面链接已复制到剪切板: ' + pageUrl);
            }).catch(function () {
                ttx.toast.info('页面链接: ' + pageUrl);
            });
        }
        ttx.toast.info('页面链接: ' + pageUrl);
    }),

    /** 进入 / 退出全屏（requestFullscreen 返回 Promise，需透传供 wrapCommand 捕获错误） */
    fullscreen: wrapCommand('FULLSCREEN', function () {
        if (document.fullscreenElement) {
            return document.exitFullscreen?.()
                || document.webkitExitFullscreen?.();
        } else {
            var el = document.documentElement;
            return el.requestFullscreen?.()
                || el.webkitRequestFullscreen?.()
                || el.msRequestFullscreen?.();
        }
    }),

    /** PWA 安装：已安装则提示成功，未触发原生事件则引导手动安装，否则弹出原生安装提示 */
    install: wrapCommand('INSTALL', function () {
        var isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || navigator.standalone;
        if (isStandalone) {
            ttx.toast.success('应用已安装');
            return;
        }
        var promptEvent = window.__pwaInstallPrompt;
        if (promptEvent) {
            promptEvent.prompt();
            return promptEvent.userChoice.then(function (choice) {
                window.__pwaInstallPrompt = null;
                ttx.log.success('PWA 安装结果: ' + choice.outcome);
            });
        }
        // 既不是 standalone 也没有 beforeinstallprompt → 可能浏览器不支持或已安装
        ttx.toast.info('请通过浏览器菜单 > "添加到桌面" 安装');
    }),

    /** 发送系统通知（Web Notification API） */
    notify: wrapCommand('NOTIFY', function (ctx) {
        if (!('Notification' in window)) {
            ttx.toast.warning('当前浏览器不支持系统通知');
            return;
        }
        if (Notification.permission === 'denied') {
            ttx.toast.warning('通知权限已被拒绝，请在浏览器设置中开启');
            return;
        }
        var send = function () {
            var data = getPageData(ctx);
            new Notification(data.notifyTitle || document.title || 'Meoo 通知', {
                body: data.notifyBody || '',
                icon: data.notifyIcon || '/icons/icon-192.png'
            });
        };
        if (Notification.permission === 'granted') {
            send();
        } else {
            return Notification.requestPermission().then(function (perm) {
                if (perm === 'granted') send();
            });
        }
    }),

    /** 复制文本到系统剪切板 */
    copyToClipboard: wrapCommand('COPY_TO_CLIPBOARD', function (ctx) {
        var data = getPageData(ctx);
        var text = data.clipboardText || ctx?.button?.clipboardText || '';
        if (!text) {
            ttx.toast.warning('没有可复制的内容');
            return;
        }
        if (navigator.clipboard?.writeText) {
            return navigator.clipboard.writeText(text).then(function () {
                ttx.toast.success('已复制到剪切板');
            }).catch(function () {
                ttx.toast.error('复制失败');
            });
        } else {
            // 降级方案
            var ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            ttx.toast.success('已复制到剪切板');
        }
    }),

    /** 屏幕常亮（阻止自动锁屏），再次调用则释放 */
    wakeLock: wrapCommand('WAKE_LOCK', function () {
        if (!('wakeLock' in navigator)) {
            ttx.toast.warning('当前浏览器不支持屏幕常亮');
            return;
        }
        if (_wakeLockSentinel) {
            releaseWakeLock();
            ttx.toast.success('屏幕常亮已关闭');
            return;
        }
        return navigator.wakeLock.request('screen').then(function (sentinel) {
            _wakeLockSentinel = sentinel;
            _wakeLockSentinel.addEventListener('release', function () {
                _wakeLockSentinel = null;
            });
            ttx.toast.success('屏幕常亮已开启');
        }).catch(function () {
            ttx.toast.warning('屏幕常亮请求失败');
        });
    }),

    /** 振动反馈（移动端） */
    vibrate: wrapCommand('VIBRATE', function (ctx) {
        if (!navigator.vibrate) {
            return;
        }
        var duration = ctx?.button?.vibrateDuration || 200;
        navigator.vibrate(Number(duration) || 200);
    }),

    /** 打开文件选择器，读取文件内容后通过回调返回 */
    openFile: wrapCommand('OPEN_FILE', function (ctx) {
        var accept = ctx?.button?.accept || '*/*';
        var input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.onchange = function () {
            var file = input.files?.[0];
            if (!file) return;
            var reader = new FileReader();
            reader.onload = function () {
                if (ctx?.callback) {
                    ctx.callback({ name: file.name, size: file.size, type: file.type, content: reader.result });
                }
            };
            reader.readAsDataURL(file);
        };
        input.click();
    }),

    /** 保存内容到本地文件（File System Access API，支持降级下载） */
    saveFile: wrapCommand('SAVE_FILE', function (ctx) {
        var data = getPageData(ctx);
        var content = data.saveFileContent || ctx?.button?.saveFileContent || '';
        var filename = data.saveFileName || ctx?.button?.saveFileName || 'download.txt';
        var mimeType = data.saveFileType || 'text/plain';
        var billName = ctx?.vm?.getCache?.('billName') || ctx?.vm?.getCache?.('title') || '';

        // 如果是由 export 命令触发的，复用其任务 ID
        var taskId = ctx?.vm?._exportTaskId;
        if (taskId) {
            delete ctx.vm._exportTaskId;
            window.dispatchEvent(new CustomEvent('ttx:download:update', {
                detail: { id: taskId, name: filename, billName: billName, size: content ? content.length : 0, progress: 20 }
            }));
        } else {
            taskId = 'save_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
            window.dispatchEvent(new CustomEvent('ttx:download:add', {
                detail: { id: taskId, name: filename, billName: billName, status: 'downloading', progress: 0, size: content ? content.length : 0 }
            }));
        }

        // 尝试 File System Access API
        if (window.showSaveFilePicker) {
            return window.showSaveFilePicker({
                suggestedName: filename,
                types: [{ accept: { [mimeType]: ['.' + (filename.split('.').pop() || 'txt')] } }]
            }).then(function (handle) {
                return handle.createWritable().then(function (writer) {
                    return writer.write(content).then(function () {
                        return writer.close();
                    });
                });
            }).then(function () {
                window.dispatchEvent(new CustomEvent('ttx:download:update', {
                    detail: { id: taskId, status: 'completed', progress: 100 }
                }));
            }).catch(function (err) {
                if (err.name !== 'AbortError') {
                    ttx.toast.error('保存失败: ' + (err.message || '未知错误'));
                    window.dispatchEvent(new CustomEvent('ttx:download:update', {
                        detail: { id: taskId, status: 'failed' }
                    }));
                } else {
                    // 用户取消，移除任务
                    window.dispatchEvent(new CustomEvent('ttx:download:remove', {
                        detail: { id: taskId }
                    }));
                }
            });
        }

        // 降级：download 链接
        var blob = new Blob([content], { type: mimeType });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        // 降级模式下无法准确获知下载完成时机，模拟延迟后标记完成
        setTimeout(function () {
            window.dispatchEvent(new CustomEvent('ttx:download:update', {
                detail: { id: taskId, status: 'completed', progress: 100 }
            }));
        }, 2000);
    }),
});

// 监听 PWA 安装事件，缓存供 install 命令使用
window.addEventListener('beforeinstallprompt', function (e) {
    e.preventDefault();
    window.__pwaInstallPrompt = e;
});
